import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { UserPlus, Trash2, Copy, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { relativeTime } from "@/lib/utils";

const ROLE_OPTIONS = ["owner", "admin", "member"];

export default function TeamPage() {
  const [data, setData] = useState(null);
  const [openInvite, setOpenInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [lastInvite, setLastInvite] = useState(null);

  const load = () => api.get("/team/members").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const sendInvite = async () => {
    if (!inviteEmail) return;
    try {
      const { data: res } = await api.post("/team/invites", { email: inviteEmail, role: inviteRole });
      const link = `${window.location.origin}/invite/${res.invite_token}`;
      setLastInvite({ ...res, full_link: link });
      setInviteEmail("");
      toast.success("Invite created");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invite failed");
    }
  };

  const updateRole = async (membership_id, role) => {
    try { await api.patch(`/team/members/${membership_id}`, { role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const removeMember = async (membership_id) => {
    if (!window.confirm("Remove this member?")) return;
    try { await api.delete(`/team/members/${membership_id}`); toast.success("Member removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const revokeInvite = async (invite_id) => {
    try { await api.delete(`/team/invites/${invite_id}`); toast.success("Invite revoked"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  if (!data) return <DashboardLayout title="..."><div className="text-sm text-muted-foreground">Loading...</div></DashboardLayout>;

  return (
    <DashboardLayout
      eyebrow="Team & RBAC"
      title="Workspace members"
      actions={
        <Dialog open={openInvite} onOpenChange={setOpenInvite}>
          <DialogTrigger asChild>
            <Button className="rounded-sm" data-testid="team-invite-btn"><UserPlus className="w-4 h-4 mr-1.5" />Invite member</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Invite a teammate</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs">Email</Label>
                <Input data-testid="team-invite-email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="teammate@company.com" />
              </div>
              <div>
                <Label className="text-xs">Role</Label>
                <Select value={inviteRole} onValueChange={setInviteRole}>
                  <SelectTrigger data-testid="team-invite-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {lastInvite && (
                <div className="border border-signal-500/40 bg-signal-500/10 rounded-sm p-3 text-xs space-y-2">
                  <div className="font-medium text-signal-500">Invite link (7-day expiry)</div>
                  <div className="font-mono break-all text-muted-foreground">{lastInvite.full_link}</div>
                  <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(lastInvite.full_link); toast.success("Copied"); }} data-testid="team-copy-invite-link">
                    <Copy className="w-3 h-3 mr-1" /> Copy
                  </Button>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button onClick={sendInvite} data-testid="team-send-invite-btn">Create invite link</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      }
    >
      <div className="grid grid-cols-2 lg:grid-cols-4 grid-borders bg-card/30 mb-6">
        <Stat label="Members" value={data.seat_used} testid="team-stat-members" />
        <Stat label="Pending invites" value={data.seat_pending} testid="team-stat-pending" />
        <Stat label="Plan" value={data.plan} testid="team-stat-plan" />
        <Stat label="Roles" value="3" sub="owner · admin · member" testid="team-stat-roles" />
      </div>

      <section className="mb-8">
        <div className="label-eyebrow mb-3">Members</div>
        <div className="border border-border rounded-sm overflow-hidden">
          <div className="grid grid-cols-12 px-5 py-3 bg-muted/40 text-[0.7rem] uppercase tracking-wider text-muted-foreground font-semibold">
            <div className="col-span-5">User</div>
            <div className="col-span-4">Email</div>
            <div className="col-span-2">Role</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>
          {data.members.map((m, i) => (
            <div key={m.membership_id} className="grid grid-cols-12 px-5 py-3 border-t border-border items-center" data-testid={`team-member-${i}`}>
              <div className="col-span-5 flex items-center gap-3">
                <div className="w-7 h-7 rounded-full border border-border bg-signal-500/15 text-signal-500 flex items-center justify-center text-xs font-bold">{m.name?.[0] || m.email?.[0]}</div>
                <div className="text-sm font-medium">{m.name}</div>
              </div>
              <div className="col-span-4 text-sm text-muted-foreground">{m.email}</div>
              <div className="col-span-2">
                <Select value={m.role} onValueChange={(v) => updateRole(m.membership_id, v)}>
                  <SelectTrigger className="h-8 w-32" data-testid={`team-role-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-1 text-right">
                {m.role !== "owner" && (
                  <button onClick={() => removeMember(m.membership_id)} data-testid={`team-remove-${i}`}
                    className="w-8 h-8 inline-flex items-center justify-center border border-border rounded-sm hover:border-destructive/40 hover:text-destructive">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {data.pending_invites.length > 0 && (
        <section>
          <div className="label-eyebrow mb-3">Pending invitations</div>
          <div className="border border-border rounded-sm overflow-hidden">
            {data.pending_invites.map((inv, i) => (
              <div key={inv.id} className="grid grid-cols-12 px-5 py-3 border-t border-border first:border-t-0 items-center" data-testid={`team-invite-row-${i}`}>
                <div className="col-span-5 text-sm">{inv.email}</div>
                <div className="col-span-3 text-xs text-muted-foreground capitalize">{inv.role}</div>
                <div className="col-span-3 text-xs text-muted-foreground">{relativeTime(inv.created_at)}</div>
                <div className="col-span-1 text-right">
                  <button onClick={() => revokeInvite(inv.id)} className="text-xs text-destructive hover:underline" data-testid={`team-revoke-${i}`}>Revoke</button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="mt-8 p-4 border border-border rounded-sm bg-muted/20 flex items-start gap-3 text-xs text-muted-foreground">
        <ShieldCheck className="w-4 h-4 text-signal-500 flex-shrink-0 mt-0.5" />
        <div>
          <strong className="text-foreground">RBAC enforcement is live.</strong> Only owners and admins can invite, change roles, remove members,
          or edit integration credentials. The last owner cannot be demoted or removed.
        </div>
      </div>
    </DashboardLayout>
  );
}

function Stat({ label, value, sub, testid }) {
  return (
    <div className="p-6" data-testid={testid}>
      <div className="label-eyebrow mb-2">{label}</div>
      <div className="text-3xl font-display font-semibold tracking-tightest capitalize">{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}
